<template>
    <div class="reservation-table-container">
        <div class="table-header">
            <div class="title-section">
                <h3 class="table-title poppinsSemiBold" :style="{ fontSize: normalizeFontSize(15) }">{{ title }}</h3>
            </div>
            <button v-if="showViewCalendar" class="btn-view" @click="$emit('view-calendar')">
                {{ viewButtonText }}
            </button>
        </div>

        <div class="table-wrapper" ref="tableWrapper" @scroll="handleScroll">
            <table class="reservation-table">
                <thead>
                    <tr>
                        <th class="text-left">{{ $t('dashboard.tables.hour') }}</th>
                        <th class="text-left">{{ $t('dashboard.tables.reservation') }}</th>
                        <th class="text-left">{{ $t('dashboard.tables.client') }}</th>
                        <th class="text-center">{{ $t('dashboard.tables.count') }}</th>
                        <th v-if="showStatus" class="text-center">{{ $t('dashboard.tables.status') }}</th>
                        <th v-if="showNote" class="text-left">{{ $t('dashboard.tables.note') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(reservation, index) in reservations"
                        :key="`reservation-${reservation.id}-${index}`"
                        :class="{ highlighted: index % 2 === 0 }"
                        @mouseenter="hoveredId = reservation.id"
                        @mouseleave="hoveredId = null"
                    >
                        <td class="time-cell text-left">{{ formatTime(reservation) }}</td>
                        <td class="reservation-cell text-left">
                            <a :href="getReservationLink(reservation)" class="reservation-link">
                                {{ getReservationName(reservation) }}
                            </a>
                        </td>
                        <td class="client-cell text-left">
                            <a :href="getClientLink(reservation)" class="client-link">
                                {{ getClientName(reservation) }}
                            </a>
                        </td>
                        <td class="count-cell text-center">{{ getPersonCount(reservation) }}</td>
                        <td v-if="showStatus" class="status-cell text-center">
                            <span class="status-badge" :class="getStatusClass(reservation)">
                                {{ getStatusLabel(reservation) }}
                            </span>
                        </td>
                        <td v-if="showNote" class="note-cell text-left">
                            <span v-if="reservation.note" class="note-text">
                                {{ truncateNote(reservation.note) }}
                            </span>
                        </td>
                    </tr>
                    <tr v-if="reservations.length === 0">
                        <td :colspan="colspanCount" class="empty-cell">{{ $t('dashboard.tables.empty') }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeFontSize from '../../../../utils/normalizeFontSize'
export default {
    name: 'ReservationTable',
    props: {
        title: {
            type: String,
            default: "Ils arrivent aujourd'hui",
        },
        reservations: {
            type: Array,
            default: () => [],
        },
        showStatus: {
            type: Boolean,
            default: true,
        },
        showNote: {
            type: Boolean,
            default: true,
        },
        showViewCalendar: {
            type: Boolean,
            default: true,
        },
        viewButtonText: {
            type: String,
            default: 'Voir le calendrier',
        },
    },
    emits: ['view-calendar', 'load-more'],
    data() {
        return {
            hoveredId: null,
            isLoadingMore: false,
        }
    },
    computed: {
        colspanCount() {
            let count = 4 // Hour, Reservation, Client, Count
            if (this.showStatus) count++
            if (this.showNote) count++
            return count
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        handleScroll() {
            const wrapper = this.$refs.tableWrapper
            if (!wrapper || this.isLoadingMore) return

            const scrollTop = wrapper.scrollTop
            const scrollHeight = wrapper.scrollHeight
            const clientHeight = wrapper.clientHeight

            // Trigger load more when user scrolls to 80% of the content
            const scrollPercentage = (scrollTop + clientHeight) / scrollHeight
            if (scrollPercentage > 0.8) {
                this.isLoadingMore = true
                this.$emit('load-more')
                // Reset after a delay to allow new data to load
                setTimeout(() => {
                    this.isLoadingMore = false
                }, 1000)
            }
        },
        formatTime(reservation) {
            const slot = reservation.relevant_slot || reservation.booking?.time_slots?.[0]
            if (slot?.start) {
                const date = new Date(slot.start * 1000)
                const hours = date.getHours().toString().padStart(2, '0')
                const minutes = date.getMinutes().toString().padStart(2, '0')
                return `${hours}:${minutes}`
            }
            return '--:--'
        },
        getReservationName(reservation) {
            const slotItem = reservation.relevant_slot?.order_items
            if (slotItem?.itemable?.name) {
                return slotItem.itemable.name
            }
            return reservation.items?.[0]?.itemable?.name || this.$t('dashboard.tables.reservation_id', { id: reservation.id })
        },
        getReservationLink(reservation) {
            return `/back-office/v3/caisse?order=${reservation.id}`
        },
        getClientName(reservation) {
            const user = reservation.user
            if (!user) return this.$t('dashboard.tables.anonymous')
            if (user.is_guest === 1 || user.is_guest === true) return this.$t('dashboard.tables.anonymous')
            const firstName = user.first_name
            const lastName = user.last_name
            if ((!firstName || firstName === '-') && (!lastName || lastName === '-')) return this.$t('dashboard.tables.anonymous')
            if (firstName?.toLowerCase().includes('passage') || lastName?.toLowerCase().includes('passage'))
                return this.$t('dashboard.tables.anonymous')
            const firstNameDisplay =
                firstName && firstName !== '-' ? firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase() : ''
            const lastNameDisplay = lastName && lastName !== '-' ? lastName.toUpperCase() : ''
            if (!firstNameDisplay && !lastNameDisplay) return this.$t('dashboard.tables.anonymous')
            return `${firstNameDisplay} ${lastNameDisplay}`.trim()
        },
        getClientLink(reservation) {
            const user = reservation.user
            if (!user?.id) return '#'
            if (user.is_guest === 1 || user.is_guest === true) return '#'
            const firstName = user.first_name
            const lastName = user.last_name
            if ((!firstName || firstName === '-') && (!lastName || lastName === '-')) return '#'
            if (firstName?.toLowerCase().includes('passage') || lastName?.toLowerCase().includes('passage')) return '#'
            return `/back-office/v3/customer/${user.id}/edit`
        },
        getPersonCount(reservation) {
            const slotItem = reservation.relevant_slot?.order_items
            if (slotItem?.nb_contributor) {
                return slotItem.nb_contributor
            }
            return reservation.items?.[0]?.nb_contributor || reservation.booking?.nb_persons || 0
        },
        getStatusClass(reservation) {
            const status = this.getStatus(reservation)
            return {
                paid: status === 'paid',
                pending: status === 'pending' || status === 'partial',
                cancelled: status === 'cancelled',
                noshow: status === 'noshow',
            }
        },
        getStatus(reservation) {
            if (reservation.status === 'cancelled') return 'cancelled'
            if (reservation.is_no_show) return 'noshow'
            if (parseFloat(reservation.current_price) >= parseFloat(reservation.total_price)) return 'paid'
            if (parseFloat(reservation.current_price) > 0) return 'partial'
            return 'pending'
        },
        getStatusLabel(reservation) {
            const status = this.getStatus(reservation)
            return this.$t('dashboard.statuses.' + status)
        },
        truncateNote(note, maxLength = 30) {
            if (!note) return ''
            return note.length > maxLength ? note.substring(0, maxLength) + '...' : note
        },
    },
}
</script>

<style scoped>
.reservation-table-container {
    width: 100%;
    border-radius: 20px;
    background: linear-gradient(127deg, rgba(6, 11, 40, 0.74) 24.51%, rgba(10, 14, 35, 0.71) 87.45%);
    backdrop-filter: blur(60px);
    padding: 15px 20px;
    position: relative;
    height: 35vh;
    display: flex;
    flex-direction: column;
}

#main-container {
    padding-bottom: 0 !important;
}

.table-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 15px;
}

.table-title {
    color: #fff;
    margin: 0;
}

.btn-view {
    display: flex;
    padding: 6px 16px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.btn-view:hover {
    background: rgba(255, 255, 255, 0.2);
}

.table-wrapper {
    max-height: 400px;
    overflow-y: auto;
    padding-right: 5px;
}

/* Custom scrollbar styling */
.table-wrapper::-webkit-scrollbar {
    width: 6px;
}

.table-wrapper::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
}

.table-wrapper::-webkit-scrollbar-thumb {
    background: rgba(125, 45, 189, 0.3);
    border-radius: 10px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
    background: rgba(125, 45, 189, 0.5);
}

.reservation-table {
    width: 100%;
    border-collapse: collapse;
}

.reservation-table thead tr {
    background: rgba(125, 45, 189, 0.15);
}

.reservation-table th {
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 12px;
    font-weight: 600;
    padding: 10px 12px;
    letter-spacing: 0.5px;
}

/* Colonne Heure : largeur minimale fixe */
.reservation-table th:first-child,
.time-cell {
    width: 1%;
    white-space: nowrap;
    padding-right: 8px !important;
}

/* Colonne Participants : largeur minimale fixe */
.count-cell {
    width: 1%;
    white-space: nowrap;
    padding-left: 8px !important;
    padding-right: 8px !important;
}

/* Colonne Réservation : prend le maximum d'espace disponible */
.reservation-cell {
    max-width: 0;
    width: 35%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.reservation-table th.text-left,
.reservation-table td.text-left {
    text-align: left;
}

.reservation-table th.text-center,
.reservation-table td.text-center {
    text-align: center;
}

.reservation-table tbody tr {
    border-top: 1px solid rgba(255, 255, 255, 0.05);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.reservation-table tbody tr.highlighted {
    background: #191d41;
}

.reservation-table td {
    color: #fff;
    font-family: 'Poppins', sans-serif;
    font-size: 13px;
    font-weight: 400;
    padding: 10px 15px;
}

.reservation-link,
.client-link {
    color: #fff;
    text-decoration: underline;
    transition: color 0.2s ease;
}

.reservation-link:hover,
.client-link:hover {
    color: #bc51e6;
}

.status-badge {
    display: inline-flex;
    padding: 5px 10px;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 500;
}

.status-badge.paid {
    background: #173638;
    color: #039855;
}

.status-badge.pending {
    background: #393029;
    color: #dc6803;
}

.status-badge.cancelled {
    background: #382530;
    color: #d92d20;
}

.status-badge.noshow {
    background: #37242f;
    color: #c52b21;
}

.empty-cell {
    text-align: center;
    color: #a0aec0;
    padding: 40px !important;
}

.note-text {
    color: rgba(255, 255, 255, 0.5);
    font-size: 12px;
    font-style: italic;
}
</style>
